import { useEffect, useState } from "react";

const TICK_MS = 1_000;

/** "12s", "3m", "2h" from a span in milliseconds (pure). */
export function spanAgo(ms: number): string {
	const s = Math.max(0, Math.floor(ms / 1_000));
	if (s < 60) return `${s}s`;
	if (s < 3_600) return `${Math.floor(s / 60)}m`;
	return `${Math.floor(s / 3_600)}h`;
}

export type FreshState = "none" | "fresh" | "stale" | "failing";

/** Past twice the poll interval, something is holding data back even with no error to show (pure). */
export function freshState(updatedAt: number | null, nowMs: number, refreshMs: number, failing: boolean): FreshState {
	if (updatedAt === null) return "none";
	if (failing) return "failing";
	return nowMs - updatedAt > refreshMs * 2 ? "stale" : "fresh";
}

function useNow(): number {
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		const tick = () => {
			if (document.visibilityState === "visible") setNow(Date.now());
		};
		const t = window.setInterval(tick, TICK_MS);
		document.addEventListener("visibilitychange", tick);
		return () => {
			window.clearInterval(t);
			document.removeEventListener("visibilitychange", tick);
		};
	}, []);
	return now;
}

/** How old the data on screen really is, from when it arrived. Says nothing until it has. Leads a note line. */
export function Updated({ at, refreshMs, failing = false, alone = false }: { at: number | null; refreshMs: number; failing?: boolean; alone?: boolean }) {
	const now = useNow();
	const state = freshState(at, now, refreshMs, failing);
	if (at === null || state === "none") return null;
	const age = spanAgo(now - at);
	const text = state === "failing" ? `refresh failing, showing data from ${age} ago` : `updated ${age} ago`;
	return (
		<>
			<span className={`num ${state === "fresh" ? "" : "text-warn"}`}>{text}</span>
			{alone ? "" : " · "}
		</>
	);
}
